import React, { useEffect, useState } from 'react'
import { Select, TextField } from '@boltenergy-be/design-system'
import { useFormContext } from 'react-hook-form'
import { AddressFields, AddressProps } from 'components/inputs/address/types'
import { useTranslation } from 'next-i18next'
import { POSTAL_CODE } from 'constants/regex'
import axios from 'axios'
import { NextApiResponseBody } from 'types/boltApi/request'
import { GetTownsResponse, Towns } from 'types/towns'
import { updateInput } from 'store/customer/slice'
import { InputGroupKeys, MeterDetailsFieldKeys, PersonalDataFieldKeys, SimulationFieldKeys } from 'store/customer/enums'
import { Region } from 'types/region'
import { CounterTypes } from 'features/simulation/types'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import { getFieldError, getFieldName } from 'components/inputs/utils'

import styles from './PostalCodeAndTown.module.scss'

/**
 * Address component
 * @remarks: This component is used to render the postal code & town fields in a form. The inputs required for the RHF are:
 * - postalCode
 * - townName
 * - townCode
 *
 * @param {AddressProps} param0
 */
const PostalCodeAndTown = ({
  grid,
  hasRegionField,
  hiddenLabel,
  hideTownSelectField,
  parentDotPath,
  required = true,
  setFetchingTowns,
  hideErrorMessage
}: AddressProps) => {
  // Redux store
  const { inputs } = useStoreSelector((store) => store.customer)
  const dispatch = useStoreDispatch()

  // Local state
  const [towns, setTowns] = useState<Towns>([])
  const [localFetchingTowns, setLocalFetchingTowns] = useState<boolean>(false)
  const [fetchTownsError, setFetchTownsError] = useState<boolean>(false)

  // React Hook Form
  const {
    formState: { errors },
    register,
    setError,
    setValue,
    watch
  } = useFormContext()
  const watchTownName = watch(getFieldName(AddressFields.TOWN_NAME, parentDotPath))

  // i18n
  const { t } = useTranslation(['commonFormFields', 'validation', 'common'])

  /**
   * Triggered on first render
   * Checks if town names should be fetched based on postalCode in store
   */
  useEffect(() => {
    initialize()
  }, [])

  /**
   * Fetches the towns
   *
   * @param {string} postalCode
   */
  const fetchTowns = async (postalCode: string) => {
    setFetchTownsError(false)

    hideTownSelectField ? setFetchingTowns(true) : setLocalFetchingTowns(true)
    try {
      // Check if the postal code is valid before fetching
      if (POSTAL_CODE.test(postalCode)) {
        // Fetch the towns & the region
        const {
          data: { data: getTownsData }
        } = await axios.get<NextApiResponseBody<GetTownsResponse>>(`/api/towns/${postalCode}`)
        const { towns: fetchedTowns, region } = getTownsData

        // Set town local state
        setTowns(fetchedTowns)

        // Set default town name & code
        setValue(getFieldName(AddressFields.TOWN_CODE), fetchedTowns[0].townCode || 0)
        setValue(getFieldName(AddressFields.TOWN_NAME), fetchedTowns[0].townName || '')

        // Set the region if the field is present
        if (hasRegionField) {
          setValue(AddressFields.REGION, region)
        }

        // Trigger store ypdate based on region
        updateStoreDataBasedOnRegion(region)
      }
    } catch (error) {
      setFetchTownsError(true)
      setError(getFieldName(AddressFields.POSTAL_CODE, parentDotPath), { type: 'api', message: t('common:error') })
    } finally {
      hideTownSelectField ? setFetchingTowns(false) : setLocalFetchingTowns(false)
    }
  }

  /**
   * Initializes the form by fetching & setting towns if necessary
   */
  const initialize = async () => {
    // Fetch the postal code & town name from the store
    const postalCode = inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.DELIVERY_ADDRESS][AddressFields.POSTAL_CODE]
    const townName = inputs[InputGroupKeys.PERSONAL_DATA][PersonalDataFieldKeys.DELIVERY_ADDRESS][AddressFields.TOWN_NAME]

    if (postalCode) {
      // Fetch the towns if the postal code is valid
      await fetchTowns(postalCode)

      // Set the town name & code to the store value if towns are found & store already has a value
      if (towns.length && townName) {
        handleTownSelectChange(townName)
      }
    }
  }

  /**
   * Handles the town select change & sets the town code
   *
   * @param {string} selectedTown
   */
  const handleTownSelectChange = (selectedTown: string) => {
    const t = towns.find((town) => town.townName === selectedTown)

    setValue(getFieldName(AddressFields.TOWN_CODE, parentDotPath), t?.townCode || 0)
    setValue(getFieldName(AddressFields.TOWN_NAME, parentDotPath), t?.townName || '')
  }

  /**
   * Updates some store data based on the selected region
   *
   * @param {Region} region
   */
  const updateStoreDataBasedOnRegion = (region: Region) => {
    // Update the region in the store
    dispatch(updateInput({ group: InputGroupKeys.SIMULATION, key: SimulationFieldKeys.REGION, value: region }))

    // Set hasSolarPanels to false if Brussels & analog meter
    if (region === Region.BRUSSELS && inputs[InputGroupKeys.METER_DETAILS][MeterDetailsFieldKeys.COUNTER_TYPE] === CounterTypes.ANALOG) {
      dispatch(
        updateInput({
          group: InputGroupKeys.METER_DETAILS,
          key: MeterDetailsFieldKeys.HAS_SOLAR_PANELS,
          value: false
        })
      )
    }
  }

  return (
    <>
      <TextField
        {...register(getFieldName(AddressFields.POSTAL_CODE, parentDotPath), {
          onChange: (e) => fetchTowns(e.target.value),
          pattern: { value: POSTAL_CODE, message: t('commonFormFields:postalCode.errors.invalid') },
          required: {
            value: required,
            message: t('validation:required')
          },
          validate: () => !fetchTownsError || t('common:error')
        })}
        error={
          hideErrorMessage
            ? !!getFieldError(AddressFields.POSTAL_CODE, parentDotPath, errors)?.message
            : getFieldError(AddressFields.POSTAL_CODE, parentDotPath, errors)?.message
        }
        grid={grid}
        hiddenLabel={hiddenLabel}
        inputMode="numeric"
        label={t('commonFormFields:postalCode.label')}
        type="number"
        className={styles.postalCode}
      />

      {!hideTownSelectField ? (
        <Select
          {...register(getFieldName(AddressFields.TOWN_NAME, parentDotPath), {
            onChange: (e) => handleTownSelectChange(e.target.value),
            required: {
              value: required,
              message: t('validation:required')
            }
          })}
          disabled={towns.length === 0}
          error={
            hideErrorMessage
              ? !!getFieldError(AddressFields.TOWN_NAME, parentDotPath, errors)?.message
              : getFieldError(AddressFields.TOWN_NAME, parentDotPath, errors)?.message
          }
          grid={grid}
          hiddenLabel={hiddenLabel}
          label={t('commonFormFields:townName.label')}
          loading={localFetchingTowns}
          value={watchTownName}
        >
          <option disabled>{t('commonFormFields:select')}</option>
          {towns.map((town) => (
            <option key={`town.geoCode-${town.townCode}`} value={town.townName}>
              {town.townName}
            </option>
          ))}
        </Select>
      ) : (
        <input type="hidden" {...register(getFieldName(AddressFields.TOWN_NAME))} />
      )}

      <input type="hidden" {...register(getFieldName(AddressFields.TOWN_CODE))} />
    </>
  )
}

export default PostalCodeAndTown
